import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTableData } from './hooks/HData';
import { buildAverageYieldByCountry, CountryYieldRow } from './charts/DataCharts';

// Small, optional country -> lat/lon lookup for common countries. Add more as needed.
const COUNTRY_COORDS: Record<string, [number, number]> = {
    'United States': [37.0902, -95.7129],
    'United Kingdom': [55.3781, -3.4360],
    'Germany': [51.1657, 10.4515],
    'France': [46.2276, 2.2137],
    'Spain': [40.4637, -3.7492],
    'Italy': [41.8719, 12.5674],
    'Netherlands': [52.1326, 5.2913],
    'Unknown': [0, 0],
};

export default function MapTab() {
    const { data, loading, error, loadTableData } = useTableData('data');
    const [mapReady, setMapReady] = useState(false);
    const theme = useTheme();
    const tabSx = {
        color: '#123f6a', // slightly less dark navy when not selected
        '&.Mui-selected': {
            color: '#00A8FF', // electric blue when selected
        },
    };
    const [Leaflet, setLeaflet] = useState<any | null>(null);

    useEffect(() => {
        void loadTableData();
    }, [loadTableData]);

    useEffect(() => {
        // try to dynamically import react-leaflet/leaflet if installed
        let mounted = true;
        import('react-leaflet')
            .then((mod) => {
                if (!mounted) return;
                setLeaflet(mod);
                // ensure leaflet css is loaded if leaflet package present
                try {
                    // eslint-disable-next-line @typescript-eslint/no-var-requires
                    require('leaflet/dist/leaflet.css');
                } catch (e) {
                    // ignore: css may be included globally by app
                }
                setMapReady(true);
            })
            .catch(() => {
                setMapReady(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    const [countriesGeoJson, setCountriesGeoJson] = useState<any | null>(null);
    useEffect(() => {
        // fetch a small countries geojson (datasets/geo-countries)
        let mounted = true;
        (async () => {
            try {
                const resp = await fetch('https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson');
                if (!resp.ok) return;
                const gj = await resp.json();
                if (!mounted) return;
                setCountriesGeoJson(gj);
            } catch (e) {
                console.warn('[MapTab] failed to load countries geojson', e);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const [assetClass, setAssetClass] = useState<'residential' | 'office' | 'industrial'>('residential');

    const handleAssetTab = (e: React.SyntheticEvent, v: number) => {
        const map = ['residential', 'office', 'industrial'] as const;
        setAssetClass(map[v]);
    };

    const listData = buildAverageYieldByCountry(Array.isArray(data) ? data : [], assetClass) as CountryYieldRow[];

    const allValues = listData.map((r) => Number(r.avgYieldGross ?? 0));
    const overallMin = allValues.length ? Math.min(...allValues) : 0;
    const overallMax = allValues.length ? Math.max(...allValues) : 1;
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const hexToRgb = (hex: string) => {
        const h = hex.replace('#', '');
        return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
    };
    const rgbToHex = (r: number, g: number, b: number) => '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
    const interpolateColor = (lowHex: string, highHex: string, t: number) => {
        const [lr, lg, lb] = hexToRgb(lowHex);
        const [hr, hg, hb] = hexToRgb(highHex);
        const r = lerp(lr, hr, t);
        const g = lerp(lg, hg, t);
        const b = lerp(lb, hb, t);
        return rgbToHex(r, g, b);
    };
    const lowColor = '#c62828'; // deep red
    const highColor = '#2e7d32'; // deep green

    if (loading) return <Typography>Loading data…</Typography>;
    if (error) return <Typography color='error'>Failed to load data</Typography>;

    return (
        <Box sx={{ padding: 2 }}>
            <Typography variant='h6' gutterBottom>
                Country Yield Map ({assetClass.charAt(0).toUpperCase() + assetClass.slice(1)})
            </Typography>

            <Tabs value={['residential','office','industrial'].indexOf(assetClass)} onChange={handleAssetTab} sx={{ mb: 2 }}>
                <Tab label='Residential' sx={tabSx} />
                <Tab label='Office' sx={tabSx} />
                <Tab label='Industrial' sx={tabSx} />
            </Tabs>

            {mapReady && Leaflet ? (
                // render a choropleth using GeoJSON if geojson loaded, otherwise fall back to circle markers
                (() => {
                    const { MapContainer, TileLayer, CircleMarker, Popup, GeoJSON, Tooltip } = Leaflet as any;

                    const center: [number, number] = [20, 0];

                    // build a name->value map using normalized names
                    const normalize = (s: string) =>
                        String(s || '')
                            .toLowerCase()
                            .normalize('NFD')
                            .replace(/[ --]/g, '')
                            .replace(/[ - ]/g, '')
                            .replace(/[ - ]/g, '')
                            .replace(/\p{Diacritic}/gu, '')
                            .replace(/[^a-z0-9 ]/g, '')
                            .trim();

                    const normalizeSafe = (s: string) => {
                        try {
                            return String(s || '')
                                .toLowerCase()
                                .normalize('NFD')
                                .replace(/[ -\u007F-\u00FF]/g, '')
                                .replace(/[^a-z0-9 ]/g, '')
                                .trim();
                        } catch (e) {
                            return String(s || '').toLowerCase().trim();
                        }
                    };

                    const valueByName = new Map<string, number>();
                    listData.forEach((r) => valueByName.set(normalizeSafe(r.country), Number(r.avgYieldGross ?? 0)));

                    const styleFeature = (feature: any) => {
                        const name = feature?.properties?.ADMIN || feature?.properties?.NAME || feature?.properties?.name || '';
                        const val = valueByName.get(normalizeSafe(name));
                        const t = overallMax === overallMin ? 0.5 : Math.max(0, Math.min(1, (Number(val ?? 0) - overallMin) / (overallMax - overallMin)));
                        const color = interpolateColor(lowColor, highColor, t);
                        return {
                            fillColor: color,
                            color: '#444',
                            weight: 0.5,
                            fillOpacity: val == null ? 0.15 : 0.75,
                        };
                    };

                    const onEachFeature = (feature: any, layer: any) => {
                        const name = feature?.properties?.ADMIN || feature?.properties?.NAME || feature?.properties?.name || '';
                        const val = valueByName.get(normalizeSafe(name));
                        layer.bindPopup(`<strong>${name}</strong><div>Avg Yield Gross: ${val == null ? 'n/a' : val + '%'} </div>`);
                        layer.bindTooltip(`${name}: ${val == null ? 'n/a' : val + '%'}`);
                        layer.on({
                            mouseover: (e: any) => {
                                (e.target as any).setStyle({ weight: 2, color: '#000' });
                            },
                            mouseout: (e: any) => {
                                (e.target as any).setStyle(styleFeature(feature));
                            },
                        });
                    };

                    if (countriesGeoJson) {
                        return (
                            <Paper sx={{ height: 480, p: 1 }}>
                                <MapContainer center={center} zoom={2} style={{ height: '100%', width: '100%' }}>
                                    <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
                                    <GeoJSON data={countriesGeoJson} style={styleFeature} onEachFeature={onEachFeature} />
                                </MapContainer>
                            </Paper>
                        );
                    }

                    // fallback to circle markers if geojson not present
                    const markers = listData
                        .map((r) => ({ r, coord: COUNTRY_COORDS[r.country] ?? null }))
                        .filter((m) => m.coord && !(m.coord[0] === 0 && m.coord[1] === 0));

                    const vals = markers.map((m) => Number(m.r.avgYieldGross ?? 0));
                    const min = vals.length ? Math.min(...vals) : 0;
                    const max = vals.length ? Math.max(...vals) : 1;

                    return (
                        <Paper sx={{ height: 480, p: 1 }}>
                            <MapContainer center={center} zoom={2} style={{ height: '100%', width: '100%' }}>
                                <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
                                {markers.map((m, i) => {
                                    const val = Number(m.r.avgYieldGross ?? 0);
                                    const t = max === min ? 0.5 : Math.max(0, Math.min(1, (val - min) / (max - min)));
                                    const color = interpolateColor(lowColor, highColor, t);
                                    const radius = 6 + (t * 10);
                                    return (
                                        <CircleMarker key={i} center={m.coord} radius={radius} pathOptions={{ color, fillColor: color, fillOpacity: 0.8 }}>
                                            <Popup>
                                                <div>
                                                    <strong>{m.r.country}</strong>
                                                    <div>Avg Yield Gross: {m.r.avgYieldGross}%</div>
                                                </div>
                                            </Popup>
                                            <Tooltip direction='top'>{`${m.r.country}: ${m.r.avgYieldGross}%`}</Tooltip>
                                        </CircleMarker>
                                    );
                                })}
                            </MapContainer>
                        </Paper>
                    );
                })()
            ) : (
                // fallback: simple table listing countries and yields and instructions to install leaflet
                <>
                    <Typography variant='body2' sx={{ mb: 1 }}>
                        Interactive map not available. To enable the map, install <code>react-leaflet</code> and <code>leaflet</code> in the frontend:
                    </Typography>
                    <Paper sx={{ p: 1, mb: 2 }}>
                        <Typography variant='body2'>
                            <strong>Install:</strong>
                        </Typography>
                        <Typography component='pre' sx={{ whiteSpace: 'pre-wrap' }}>
                            npm install react-leaflet leaflet
                        </Typography>
                        <Typography variant='body2'>
                            Then import the Leaflet CSS in your app entry, e.g. <code>import 'leaflet/dist/leaflet.css';</code>
                        </Typography>
                    </Paper>

                    <Paper>
                        <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Country</TableCell>
                                            <TableCell>Avg Yield Gross</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {listData.map((r) => {
                                            const val = Number(r.avgYieldGross ?? 0);
                                            const t = overallMax === overallMin ? 0.5 : Math.max(0, Math.min(1, (val - overallMin) / (overallMax - overallMin)));
                                            const color = interpolateColor(lowColor, highColor, t);
                                            return (
                                                <TableRow key={r.country}>
                                                    <TableCell>
                                                        <span style={{ display: 'inline-block', width: 12, height: 12, background: color, marginRight: 8, verticalAlign: 'middle' }} />
                                                        {r.country}
                                                    </TableCell>
                                                    <TableCell>{r.avgYieldGross}%</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                        </Table>
                    </Paper>
                </>
            )}
        </Box>
    );
}
